import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { Container } from 'typescript-ioc';
import * as mobx from 'mobx';
import { IEventDetailsDTO } from '../../../../api/events.dao';
import { IRoomDetailsDTO, IRoomListDTO, IRoomsDAO } from '../../../../api/rooms.dao';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { JobType } from '../../../../api/jobs.dao';
import { IJobsMonitor } from '../../../../domain/jobs-monitor/jobs-monitor';
import { CreateZoomMeetingsVM } from '../shared/_create-zoom-meetings/create-zoom-meetings.viewmodel';
import { MINIMAL_SEARCH_LENGTH } from '../../../../constants/common.constants';
import { EventExecutionRuntime } from '../../../../domain/jobs/implementations/event-execution-runtime';
import { GenerateRoomsVM } from './_generate-rooms/generate-rooms.viewmodel';
import { RoomForm } from './_room-form/room-form';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { IConnectionDetailsDTO } from '../../../../api/connections.dao';
import { IConfigurePassportDisplayFormProps } from '../shared/configure-passport-display/configure-passport-display.viewmodel';

class RoomsViewModel {
    private operatorsUploadJobCompletedConnectionsCallback: () => void;
    @mobx.observable
    private roomIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    @mobx.observable
    public rooms: IRoomDetailsDTO[];
    @mobx.observable
    private loading: boolean;
    @mobx.observable
    private isUploadOperatorsCSVModalOpen: boolean;
    @mobx.observable
    private operatorsCSVFile;
    @mobx.observable
    public searchQuery: string;
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    private firstLoad = false;

    private externalOnChangeCallback: () => void;
    public handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void;
    public showConfigurePassportForm: (props: IConfigurePassportDisplayFormProps, connection: IConnectionDetailsDTO) => void;
    public trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;

    public generateRoomsVM: GenerateRoomsVM;
    public createZoomMeetingsVM: CreateZoomMeetingsVM;

    private paginationVM: PagingViewModel;

    constructor(
        private readonly accountId: number,
        private readonly refreshMeetingsTabAction: () => void,
        private readonly intl: IntlShape,
        @IoC.Inject private readonly roomsDAO: IRoomsDAO = Container.get(IRoomsDAO),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly jobsMonitor: IJobsMonitor = IoC.Container.get(IJobsMonitor),
        @IoC.Inject private readonly jobsPipeline: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.roomIdToSubWindowMap = {};
        this.paginationVM = new PagingViewModel();
        this.rooms = [];
        this.searchQuery = '';


        this.jobsPipeline.onComplete(JobType.GenerateRooms, this.handleCreateMultipleRooms);
        this.jobsPipeline.onComplete(JobType.OperatorsUpload, this.handleImportOperatorsJobCompletion);
        this.jobsPipeline.onComplete(JobType.RoomsUpload, this.onCreateZoomMeetingsSuccess);
        this.jobsPipeline.onComplete(JobType.GenerateRoomConnections, this.handleGenerateRoomConnections);

        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchRoomsList);
    }

    @mobx.action
    public async initialize() {
        this.generateRoomsVM = new GenerateRoomsVM(this.event, this.intl, this.handleCreateMultipleRooms);
        this.createZoomMeetingsVM = new CreateZoomMeetingsVM(this.generateRoomConnections);
    }

    public setOperatorsUploadJobCompletedConnectionsCallback(operatorsUploadJobCompletedConnectionsCallback: () => void) {
        this.operatorsUploadJobCompletedConnectionsCallback = operatorsUploadJobCompletedConnectionsCallback;
    }

    public isPipelineReady = (): boolean => this.jobsPipeline.isReady();

    public getPaginationVM = () => {
        return this.paginationVM;
    };

    public getLoading() {
        return this.loading;
    }

    public getSearchQuery = () => {
        return this.searchQuery;
    };

    public isEditFormShown(roomId: number) {
        return this.roomIdToSubWindowMap[roomId]
            && this.roomIdToSubWindowMap[roomId].getStatus() !== SubWindowStatus.Closed;
    }

    public composeUploadOperatorsButtonProperties(): {text: string; disabled: boolean} {
        return {
            text: 'Upload Operators',
            disabled: !this.jobsPipeline.isReady()
        };
    }

    @mobx.action
    public setSearchQuery = (query: string) => {
        this.searchQuery = query;

        if (query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.paginationVM.selectPage(1);
            this.fetchRoomsList();
        }
    };

    @mobx.action
    public clearSearchQuery = () => {
        this.searchQuery = '';
        this.paginationVM.selectPage(1);
        this.fetchRoomsList();
    };

    @mobx.action
    public setFirstLoad(value: boolean): void {
        this.firstLoad = value;
    }

    @mobx.action
    public fetchRoomsList = async () => {
        if (this.firstLoad) {
            this.trackingService.get().track(TRACKING_EVENTS.LOAD_ROOMS_TAB, {});
            this.firstLoad = false;
        }
        this.loading = true;
        this.roomsDAO.getRoomsList({
            accountId: this.accountId,
            eventId: this.event.eventId,
            query: this.searchQuery?.trim() || '',
            size: this.paginationVM.getPageSize(),
            page: this.paginationVM.getCurrentPageForBE()
        }).then(this.onFetchSuccess, this.onFetchError);
    };

    @mobx.action
    public updateRoom = (roomId: number, path: OpenFrom) => {
        const existingSubWindowForThisRoom = this.roomIdToSubWindowMap[roomId];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisRoom && existingSubWindowForThisRoom.getStatus() !== SubWindowStatus.Closed) {
            // if we successfully maximized existing one
            existingSubWindowForThisRoom.maximize();
            shallOpenNewSubWindow = false;
        }
        if (shallOpenNewSubWindow) {
            this.roomIdToSubWindowMap[roomId] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage({
                    id: 'oe.room.edit',
                    description: 'Popover header text',
                    defaultMessage: 'Edit Room'
                }),
                componentToHost: RoomForm,
                componentToHostProps: {
                    event: this.event,
                    accountId: this.accountId,
                    roomId: roomId,
                    saveCompletedCallback: () => {
                        if (this.externalOnChangeCallback) this.externalOnChangeCallback();
                        return this.fetchRoomsList();
                    },
                    deleteCompletionCallback: (deletedRoomId: number) => {
                        if (this.externalOnChangeCallback) this.externalOnChangeCallback();
                        return this.completeRoomDeletion(deletedRoomId);
                    },
                    openFrom: path,
                    handleTroubleshoot: (connection: IConnectionDetailsDTO) => {
                        this.handleTroubleshoot(connection, OpenFrom.EditRoomPopup);
                    },
                    showConfigurePassportForm: (connection: IConnectionDetailsDTO): void => {
                        this.showConfigurePassportForm(
                            {
                                accountId: this.accountId,
                                eventId: this.event.eventId,
                                roomId: roomId,
                                meetingConnectionId: connection.meetingConnectionId
                            },
                            connection,
                        );
                    },
                    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => {
                        this.trackHostJoinClick(eventName, OpenFrom.EditRoomPopup);
                    },
                },
            });
        }
    };

    private completeRoomDeletion = (deletedRoomId: number) => {
        this.fetchRoomsList();
        this.closeEditForm(deletedRoomId.toString());
        this.refreshMeetingsTabAction();
    };

    private closeEditForm(id: string) {
        this.roomIdToSubWindowMap[id].close();
    }

    public createRoom = (path: OpenFrom) => {
        this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.room.new',
                description: 'Popover header text',
                defaultMessage: 'New Room'
            }),
            componentToHost: RoomForm,
            componentToHostProps: {
                event: this.event,
                accountId: this.accountId,
                roomId: null,
                saveCompletedCallback: this.fetchRoomsList,
                openFrom: path,
            },
        });
    };

    public getRooms() {
        return this.rooms;
    }

    public getEvent() {
        return this.event;
    }

    public getUploadOperatorsCSVModalOpen = () => {
        return this.isUploadOperatorsCSVModalOpen;
    };

    public getOperatorsCSVFileName = () => {
        return this.operatorsCSVFile ? this.operatorsCSVFile.name : '';
    };

    @mobx.action
    public setOperatorsCSVFile = (file: File) => {
        this.operatorsCSVFile = file;
    };

    @mobx.action.bound
    public setUploadOperatorsCSVModalOpen = (value: boolean) => {
       this.isUploadOperatorsCSVModalOpen = value;
       this.operatorsCSVFile = null;
    };

    @mobx.action
    public uploadOperatorsCSV = async (): Promise<void> => {
        const job = this.jobsPipeline.getJobByType(JobType.OperatorsUpload);
        job.onComplete(this.handleImportOperatorsJobCompletion);

        try {
            await job.trigger({ file: this.operatorsCSVFile });
            this.setUploadOperatorsCSVModalOpen(false);
        } catch {
            this.setUploadOperatorsCSVModalOpen(true);
        }
    };

    @mobx.action
    private handleImportOperatorsJobCompletion = async (): Promise<void> => {
        this.closeAllRoomsModals();
        await this.fetchRoomsList();
    };

    @mobx.action
    public generateRoomConnections = async (groupId: number, additionalTopicText: string): Promise<void> => {
        const job = this.jobsPipeline.getJobByType(JobType.GenerateRoomConnections);
        job.onComplete(this.handleGenerateRoomConnections);

        const jobParams = {
            accountId: this.accountId,
            eventId: this.event.eventId,
            zoomGroupId: groupId || null,
            additionalTopicText: additionalTopicText,
        };

        await job.trigger(jobParams);
    };

    public downloadOperatorsCSV = () => {
        this.roomsDAO.getOperatorsCSV({ accountId: this.accountId, eventId: this.event.eventId });
    };

    public downloadAndTrackOperatorsCSV(path) {
        this.downloadOperatorsCSV();
        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_OPERATORS, {
            'Open From': path,
        });
    }

    @mobx.action
    private onFetchSuccess = (data: IRoomListDTO) => {
        this.loading = false;
        this.rooms = data.content;
        this.paginationVM.setTotalRecords(data.totalElements);
    };

    @mobx.action
    private onFetchError = error => {
        this.loading = false;
        const errorParsingService = Container.get<IErrorParsing>(IErrorParsing);
        errorParsingService.extractMessage(error);
    };

    @mobx.action
    public handleCreateMultipleRooms = async (): Promise<void> => {
        await this.fetchRoomsList();
        this.refreshMeetingsTabAction();
    };

    @mobx.action
    private onCreateZoomMeetingsSuccess = async (): Promise<void> => {
        await this.fetchRoomsList();
        this.closeAllRoomsModals();
    };

    @mobx.action
    private handleGenerateRoomConnections = async (): Promise<void> => {
        if (this.externalOnChangeCallback) this.externalOnChangeCallback();
        await this.fetchRoomsList();
    };

    private closeAllRoomsModals() {
        for (const roomId of Object.keys(this.roomIdToSubWindowMap)) {
            this.roomIdToSubWindowMap[roomId].close();
        }
    }

    @mobx.action
    public clearRoomsList = () => {
        this.rooms = [];
    };

    @mobx.action
    public setOnChangeCallback = (editCallback: () => void) => {
        this.externalOnChangeCallback = editCallback;
    };

    public setHandleTroubleshoot = (handleTroubleshoot: (connection: IConnectionDetailsDTO, openFrom: OpenFrom) => void): void => {
        this.handleTroubleshoot = handleTroubleshoot;
    };

    public setShowConfigurePassport = (showConfigurePassportForm: (
        props: IConfigurePassportDisplayFormProps,
        connection: IConnectionDetailsDTO,
    ) => void): void => {
        this.showConfigurePassportForm = showConfigurePassportForm;
    };

    public setTrackHostJoinClick = (trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void): void => {
        this.trackHostJoinClick = trackHostJoinClick;
    };
}

export { RoomsViewModel };
